/* eslint-disable consistent-return */
/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import {
  MonitoringBiddingTypes,
  MonitoringState,
  TradingMessageTypes,
  MonitoringRequestTypes,
} from 'store/modules/monitoring_store/types';
import TradingMessageItem from 'components/molecules/trading_message_item';
import Loader from 'components/atoms/loader';
import Select from 'react-select';
import { getConsumer } from 'services/cable';
import _ from 'underscore';
import MonitoringLotsModal from 'components/molecules/monitoring_lots_modal';
import {
  getInterestLots,
  getTradingKeywords,
  markAsRead,
} from 'store/modules/monitoring_store/actions';
import tabId from 'services/tabId';
import { ProfileState } from 'store/modules/profile_store/types';
import { SourceIdsWithLots } from 'services/source_ids_with_lots';
import companyId from 'services/company_id';
import { highlightKeywords } from 'services/monitoring_settings';
import InfiniteScroll from 'react-infinite-scroll-component';

interface TradingMessageProps {
  selectedTradingId: number;
  messageSearch?: string;
  scrollTargetMessageId?: number | null;
  setSearchResults?: React.Dispatch<React.SetStateAction<TradingMessageTypes[]>>;
  showTitle?: boolean;
}

interface lotsSelectOptionsTypes {
  value: number;
  label: string;
  color: string | null;
}

const TradingMessages = ({
  selectedTradingId,
  messageSearch,
  scrollTargetMessageId,
  setSearchResults,
  showTitle = true,
}: TradingMessageProps) => {
  const dispatch = useDispatch();

  const { monitoring, request } = useSelector<ApplicationState, MonitoringState>(
    (state) => state.storage.monitoring,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const cableApp = getConsumer();

  const [trading, setTrading] = useState<MonitoringBiddingTypes | undefined>();
  const [interestLotsModalOpened, setInterestLotsModalOpened] = useState(false);
  const [selectedLot, setSelectedLot] = useState<number | null>(null);
  const [interestLots, setInterestLots] = useState<number[]>([]);
  const [lotsSelectOptions, setLotsSelectOptions] = useState<lotsSelectOptionsTypes[]>([]);

  // Lote indicado pelo link do push (?electronic_trading_id=X&lot=N). So vale para o pregao
  // do proprio link: sem essa checagem o parametro continuaria na URL e fixaria o lote
  // tambem nos outros pregoes que o usuario abrisse depois.
  const lotFromPushLink = () => {
    const urlParams = new URLSearchParams(window.location.search);

    if (Number(urlParams.get('electronic_trading_id')) !== selectedTradingId) return null;

    const lot = Number(urlParams.get('lot'));

    return Number.isInteger(lot) && lot > 0 ? lot : null;
  };

  const pushLinkLot = useRef<number | null>(lotFromPushLink());

  const MESSAGES_PER_PAGE = 50;
  const [allMessages, setAllMessages] = useState<TradingMessageTypes[]>([]);
  const [visibleMessages, setVisibleMessages] = useState<TradingMessageTypes[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getTradingKeywords(companyId(profile)));
  }, [profile.currentCompany[tabId()]]);

  useEffect(() => {
    setTrading(monitoring.electronics_tradings.find((t) => t.id === selectedTradingId));
  }, [selectedTradingId]);

  useEffect(() => {
    const subscription = cableApp.subscriptions.create(
      {
        channel: 'MonitoredChatsChannel',
        electronic_trading_id: selectedTradingId,
        lot: selectedLot,
      },
      {
        received: (message: TradingMessageTypes) => {
          dispatch({
            type: MonitoringRequestTypes.UPDATE_TRADING_MESSAGES,
            result: message,
          });
        },
      },
    );

    return () => subscription.unsubscribe();
  }, [selectedLot, selectedTradingId]);

  useEffect(() => {
    if (!trading) return;
    if (!trading.have_new_messages) return;

    let lotToRead = trading.lots[0];
    const key = `${trading.id}_${companyId(profile)}`;
    if (monitoring.interest_lots[key]?.length > 0)
      lotToRead = monitoring.interest_lots[key][0]?.lot;

    dispatch(markAsRead(selectedTradingId, lotToRead, companyId(profile)));
  }, [trading]);

  useEffect(() => {
    if (!trading) return;
    if (!trading.have_new_messages) return;

    if (selectedLot === null && trading.lots.length > 0) return;
    if (selectedLot !== null && trading.lots.length === 0) return;

    dispatch(markAsRead(selectedTradingId, selectedLot, companyId(profile)));
  }, [selectedLot]);

  useEffect(() => {
    if (!trading) return;

    if (pushLinkLot.current !== null) {
      if (trading.lots.includes(pushLinkLot.current)) {
        setSelectedLot(pushLinkLot.current);
        return;
      }
      pushLinkLot.current = null;
    }

    if (interestLots.length !== 0) setSelectedLot(interestLots[0]);
    else if (trading.lots.length > 0) setSelectedLot(trading.lots[0]);
  }, [interestLots, trading]);

  useEffect(() => {
    if (!trading) return;
    if (SourceIdsWithLots.includes(trading.source_id))
      dispatch(getInterestLots(trading.id, companyId(profile)));
  }, [trading]);

  useEffect(() => {
    if (!trading) return;

    const key = `${selectedTradingId}_${companyId(profile)}`;
    const lots =
      monitoring.interest_lots[key]?.length > 0
        ? monitoring.interest_lots[key].map((lot) => lot.lot)
        : trading.lots;

    setInterestLots(lots);
  }, [selectedTradingId, monitoring.interest_lots, trading, request.interest_lots]);

  useEffect(() => {
    if (!trading) return;
    setLotsSelectOptions(
      interestLots.map((interestLot) => ({
        value: interestLot,
        label: `Lote ${interestLot}`,
        color: trading?.have_new_messages_lots.includes(interestLot) ? 'red' : null,
      })),
    );
  }, [interestLots, trading]);

  useEffect(() => {
    const messages = _.sortBy(
      (monitoring.monitoring_messages[selectedTradingId] || []).filter((msg) =>
        selectedLot === null ? true : [0, selectedLot].includes(msg.lot || 0),
      ),
      (msg) => Date.parse(msg.message_time || ''),
    );

    if (messageSearch && messageSearch.trim().length > 0) {
      const searchLower = messageSearch.toLowerCase();

      const matched = messages.filter((msg) =>
        msg.message_highlight?.toLowerCase().includes(searchLower),
      );

      if (setSearchResults) {
        setSearchResults(matched);
      }
    } else if (setSearchResults) {
      setSearchResults([]);
    }

    setAllMessages(messages);
    setVisibleMessages(messages.slice(-MESSAGES_PER_PAGE));
    setCurrentPage(1);
    setHasMoreMessages(messages.length > MESSAGES_PER_PAGE);
  }, [selectedLot, trading, monitoring.monitoring_messages, messageSearch]);

  const scrollToMessage = (messageId: number) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  useEffect(() => {
    if (!scrollTargetMessageId) return;

    scrollToMessage(scrollTargetMessageId);
  }, [scrollTargetMessageId]);

  const loadMoreMessages = () => {
    const nextPage = currentPage + 1;
    const start = Math.max(allMessages.length - nextPage * MESSAGES_PER_PAGE, 0);
    const end = allMessages.length - currentPage * MESSAGES_PER_PAGE;
    const nextChunk = allMessages.slice(start, end);
    setVisibleMessages((prev) => [...nextChunk, ...prev]);
    setCurrentPage(nextPage);
    if (start === 0) setHasMoreMessages(false);
  };

  const dotLotSelect = (color = undefined) => ({
    alignItems: 'center',
    display: 'flex',
    ':after': {
      backgroundColor: color,
      borderRadius: 10,
      content: '" "',
      display: 'block',
      marginLeft: 8,
      height: 8,
      width: 8,
    },
  });

  const styleLotSelect = {
    control: (styles: any) => ({ ...styles, backgroundColor: 'white' }),
    option: (styles: any, { data }: any) => ({ ...styles, ...dotLotSelect(data.color) }),
    input: (styles: any) => ({ ...styles, ...dotLotSelect() }),
    placeholder: (styles: any) => ({ ...styles, ...dotLotSelect() }),
    singleValue: (styles: any, { data }: any) => ({ ...styles, ...dotLotSelect(data.color) }),
  };

  if (
    (!monitoring.monitoring_messages[selectedTradingId] ||
      monitoring.monitoring_messages[selectedTradingId]?.length === 0) &&
    request.monitoring_messages.loaded
  )
    return (
      <div className="monitoring-body pl-3 pt-3 pb-5 text-center">
        <div className="alert alert-light w-100 border">
          <h4 className="alert-heading">Esse pregão ainda não possui chat.</h4>
          <p className="mb-0">Você será avisado por e-mail quando o chat for iniciado.</p>
        </div>
      </div>
    );

  if (!trading) return <div />;

  const showLotSelect =
    (interestLots.length > 1 || (interestLots.length === 0 && trading.lots.length > 1)) &&
    trading.lots.length > 0;
  const showInterestLotsButton = trading.lots.length > 1;

  const tradingKeywords = highlightKeywords(monitoring.trading_keywords[companyId(profile)]);

  return (
    <>
      {(showTitle || showLotSelect || showInterestLotsButton) && (
        <div className="monitoring-bar row m-0 py-2 border-bottom bg-white">
          <div className="col-md-12 d-flex">
            <div className="d-md-flex w-100 align-items-center justify-content-center justify-content-md-start">
              {showTitle && <strong>Mensagens do chat</strong>}
              {showLotSelect && (
                <div className="col-md-3 col-12 px-0 my-2 my-md-0 px-md-3">
                  <Select
                    defaultValue={{ value: selectedLot, label: `Lote ${selectedLot}` }}
                    name="SelectLot"
                    closeMenuOnSelect
                    isClearable={false}
                    value={{
                      value: selectedLot || 0,
                      label: selectedLot ? `Lote ${selectedLot}` : 'Lote',
                    }}
                    onChange={(selected) => {
                      pushLinkLot.current = null;
                      setSelectedLot(selected?.value || null);
                    }}
                    options={lotsSelectOptions}
                    styles={styleLotSelect}
                  />
                </div>
              )}
              {showInterestLotsButton && (
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  onClick={() => {
                    pushLinkLot.current = null;
                    setInterestLotsModalOpened(true);
                  }}
                >
                  Selecionar lotes de interesse
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="monitoring monitoring-body message-list pl-3 pt-3 pb-5" id="tradingMessage">
        {(request.monitoring_messages.loading ||
          monitoring.interest_lots[`${trading?.id}_${companyId(profile)}`] === undefined) &&
          visibleMessages.length === 0 && (
            <div
              className="d-flex justify-content-center align-items-start w-100"
              style={{ height: '300px' }}
            >
              <Loader />
            </div>
          )}

        {visibleMessages.length > 0 && (
          <InfiniteScroll
            dataLength={visibleMessages.length}
            next={loadMoreMessages}
            hasMore={hasMoreMessages}
            inverse
            loader={<Loader />}
            scrollableTarget="tradingMessage"
          >
            {visibleMessages.map((tradingMessage) => (
              <TradingMessageItem
                key={`trading_message_${tradingMessage.id}`}
                tradingMessage={tradingMessage}
                tradingKeywords={tradingKeywords}
              />
            ))}
          </InfiniteScroll>
        )}
      </div>
      <MonitoringLotsModal
        setInterestLotsModalOpened={setInterestLotsModalOpened}
        interestLotsModalOpened={interestLotsModalOpened}
        selectedTradingId={selectedTradingId}
        trading={trading}
      />
    </>
  );
};

export default TradingMessages;
